import { Services, getServiceNameViaPoint, isEmpty, reportManagerCheckFilterByHasFields, reportManagerFieldToServiceMap, reportManagerFilterByOwnerFieldKeys, reportManagerFilterBySchema, reportManagerIsReportTypeWithFilterBy } from '@sigma-frontend-libs/commons'
import { EReportType } from '@sigmasms/shared-types'

import { components, isArrayEmpty, permissionPresets, proxy } from '@/utils'

import selectorOwner from '@/components/selector/owner'
import selectorSendername from '@/components/selector/sendername'
import selectorSendingStatus from '@/components/selector/sendingStatus'
import selectorSendingType from '@/components/selector/sendingType'

import render from './render'

const widgetServiceNameViaPoint = getServiceNameViaPoint(Services['otp-settings/widgets'])
const sendingsReportsWithTypeSelector = new Set([
  EReportType.sendingsGroup001,
  EReportType.sendingsGroup002,
  EReportType.sendingsGroup001Clickhouse,
  EReportType.sendingsGroup002Clickhouse,
  EReportType.sendingsGroup003Clickhouse,
  EReportType.sendingsGroup004Clickhouse,
  EReportType.sendingsPackageBilling,
  EReportType.sendingsDetailsClickhouse
])
const sendingsReportsWithStatusSelector = new Set([
  EReportType.sendingsGroup001,
  EReportType.sendingsGroup002,
  EReportType.sendingsGroup001Clickhouse,
  EReportType.sendingsGroup002Clickhouse,
  EReportType.sendingsGroup003Clickhouse,
  EReportType.sendingsGroup004Clickhouse,
  EReportType.sendingsDetailsClickhouse
])

export default {
  name: 'FilterBy',

  mixins: [ proxy({ type: 'object' }) ],

  props: {
    type: {
      type: String,
      default: undefined
    }
  },

  computed: {
    /**
     * Возвращает схему доступных полей filterBy для выбранного типа отчёта.
     *
     * @returns {Record<string, { type?: string }>}
     */
    fields() {
      if (!this.type || !reportManagerIsReportTypeWithFilterBy(this.type)) {
        return {}
      }

      return reportManagerFilterBySchema[this.type].properties ?? {}
    },

    hasFields() {
      return reportManagerCheckFilterByHasFields({
        reportType: this.type,
        fields: this.fields,
        canGetUsers: this.canGetUsers,
        canGetProviders: this.canGetProviders,
        canGetWidgets: this.canGetWidgets,
        canGetPatterns: this.canGetPatterns
      })
    },

    requiredFields() {
      if (!this.type || !reportManagerIsReportTypeWithFilterBy(this.type)) {
        return []
      }

      const result = reportManagerFilterBySchema[this.type].required ?? []

      return Array.from(result)
    },
    requiredFieldsValidation() {
      return this.requiredFields.every(field => {
        const value = this.proxy?.[field]

        return !isEmpty(value)
      })
    },

    canGetUsers() {
      return this.checkPermissions(`advanced.${Services.users}.get`, permissionPresets.resellerUp)
    },
    canGetProviders() {
      return this.checkPermissions(`advanced.${Services.providers}.get`, permissionPresets.resellerUp)
    },
    canGetWidgets() {
      return this.checkPermissions(`advanced.${widgetServiceNameViaPoint}.get`, permissionPresets.meUp)
    },
    canGetPatterns() {
      return this.checkPermissions(`advanced.${Services.patterns}.get`, permissionPresets.resellerUp)
    }
  },

  methods: {
    /**
     * Подчищает выходной filterBy перед отдачей наружу.
     * Нужен для случаев, когда селекты оставляют пустые массивы.
     *
     * @param {Record<string, unknown>} data
     * @returns {Record<string, unknown>}
     */
    _outputFilter(data) {
      if ('type' in data) {
        if (Array.isArray(data.type) && isArrayEmpty(data.type)) {
          delete data.type
        }
      }

      return data
    },
    /**
     * Хранит в proxy только значимые значения поля filterBy.
     * Пустые значения удаляются, чтобы кнопка состояния и валидация не врали.
     *
     * @param {string} field
     * @param {unknown} value
     * @returns {void}
     */
    setFieldValue(field, value) {
      if (isEmpty(value)) {
        this.$delete(this.proxy, field)

        return
      }

      if (Array.isArray(value) && isArrayEmpty(value)) {
        this.$delete(this.proxy, field)

        return
      }

      this.$set(this.proxy, field, value)
    },
    /**
     * Возвращает конфигурацию селектора типов отправлений
     * для всех sendings-based отчётов.
     *
     * @param {Record<string, unknown>} baseProps
     * @param {Record<string, Function>} on
     * @returns {{ component: unknown, props: Record<string, unknown>, on: Record<string, Function> }}
     */
    createSendingTypeConfig(baseProps, on) {
      return {
        component: selectorSendingType,
        props: {
          ...baseProps,
          permission: `advanced.${Services.sendings}.find`,
          multiple: true
        },
        on
      }
    },
    /**
     * Возвращает специализированный контрол для sendings-полей,
     * если backend-схема подразумевает enum-like selector, а не обычный input.
     *
     * @param {string} field
     * @param {Record<string, unknown>} baseProps
     * @param {Record<string, Function>} on
     * @returns {{ component: unknown, props: Record<string, unknown>, on: Record<string, Function> } | undefined}
     */
    getSendingsFieldConfig(field, baseProps, on) {
      if (sendingsReportsWithTypeSelector.has(this.type) && field === 'type') {
        return this.createSendingTypeConfig(baseProps, on)
      }

      if (sendingsReportsWithStatusSelector.has(this.type) && field === 'status') {
        return {
          component: selectorSendingStatus,
          props: { ...baseProps },
          on
        }
      }

      return undefined
    },
    /**
     * Подбирает компонент и его свойства для конкретного поля filterBy.
     * Здесь сосредоточена логика связки схемы отчёта и конкретных селекторов.
     *
     * @param {string} field
     * @param {string | undefined} type
     * @returns {{ component: unknown, props: Record<string, unknown>, on: Record<string, Function> } | undefined}
     */
    getRenderConfig(field, type) {
      let required = false
      if (reportManagerIsReportTypeWithFilterBy(this.type)) {
        required = (reportManagerFilterBySchema[this.type].required ?? []).includes(field)
      }

      const value = this.proxy[field]
      const label = this.getTranslate(`${Services['report-manager']}.labels.${this.type}.${field}`)
      const baseProps = {
        value,
        label,
        required,
        dense: true,
        clearable: !required
      }
      const commonSelectProps = {
        setRequiredOnMounted: false
      }
      const on = {
        input: event => {
          this.setFieldValue(field, event)
        }
      }
      const sendingsFieldConfig = this.getSendingsFieldConfig(field, baseProps, on)
      if (sendingsFieldConfig) {
        return sendingsFieldConfig
      }

      if (type === 'boolean') {
        return {
          component: components.switch,
          props: {
            value,
            label,
            outline: true
          },
          on
        }
      }

      if (type !== 'string') {
        return undefined
      }

      if (reportManagerFilterByOwnerFieldKeys.has(field)) {
        return {
          component: selectorOwner,
          props: {
            ...baseProps,
            ...commonSelectProps,
            combobox: true
          },
          on
        }
      }

      if (field === 'sender') {
        return {
          component: selectorSendername,
          props: {
            ...baseProps,
            ...commonSelectProps,
            required: false
          },
          on
        }
      }

      if (field === 'provider') {
        if (!this.canGetProviders) {
          return undefined
        }

        return {
          component: components.select,
          props: {
            ...baseProps,
            ...commonSelectProps,
            service: reportManagerFieldToServiceMap[field],
            query: { $order: [ [ 'isActive', 'desc' ] ] },
            autocomplete: true,
            details: false
          },
          on
        }
      }

      if (field === 'widgetId') {
        if (!this.canGetWidgets) {
          return undefined
        }

        return {
          component: components.select,
          props: {
            ...baseProps,
            ...commonSelectProps,
            service: reportManagerFieldToServiceMap[field],
            pick: [ 'id', 'name' ],
            itemTitle: 'name',
            autocomplete: true,
            details: false
          },
          on
        }
      }

      if (field === 'patternId') {
        if (!this.canGetPatterns) {
          return undefined
        }

        return {
          component: components.select,
          props: {
            ...baseProps,
            ...commonSelectProps,
            service: reportManagerFieldToServiceMap[field],
            pick: [ 'id', 'title' ],
            itemTitle: 'title',
            autocomplete: true,
            details: false
          },
          on
        }
      }

      return {
        component: components['text-field'],
        props: {
          ...baseProps,
          details: false
        },
        on
      }
    }
  },

  render
}

